import { createClass as _createClass, classCallCheck as _classCallCheck, defineProperty as _defineProperty, toConsumableArray as _toConsumableArray } from '../../_virtual/_rollupPluginBabelHelpers.js';

var MOCK_REQUEST = "MOCK_REQUEST";
/**
 * Generic action creator for mocking API requests
 * @param {Object} options Specification for the mock request
 * @param {string} options.type Redux action type
 * @param {boolean} options.success Whether the mock request should return a success (HTTP 200) response
 * @param {*} options.response The response data to return for a successful mock request
 * @param {*} options.error The error to return
 * @param {number} options.delay Optional, time delay (ms) for the mock request to take, default 100ms
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number} options.actionSlowDelay Optional, time delay (ms) for an additional action to be dispatched to indicate the request is taking longer than expected, default 200ms
 * @param {boolean} options.preventErrorNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {boolean} options.preventResponseHandling Optional, prevents the default response handling, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */

var mockRequest = function mockRequest(options) {
  return {
    type: MOCK_REQUEST,
    requestType: options.type,
    success: options.success,
    response: options.response,
    error: options.error,
    delay: options.delay !== undefined ? options.delay : 100,
    actionCustomData: options.actionCustomData,
    actionSlowDelay: options.actionSlowDelay !== undefined ? options.actionSlowDelay : 200,
    preventErrorNotification: options.preventErrorNotification || false,
    preventResponseHandling: options.preventResponseHandling || false,
    debounce: typeof options.debounce === "number" ? options.debounce : false,
    abort: options.abort
  };
};
/**
 * Action creator for mocking a successful api request
 * @param {string} type Redux action type
 * @param {Object} options Optional, additional options to pass to the request
 * @param {*} options.response The response data to return
 * @param {number} options.delay Optional, time delay (ms) for the mock request to take, default 100ms
 * @param {*} options.actionCustomData Optional, additional data that can be added to the Redux action and will be available in a reducer as an action.customData property
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */


var mockSuccess = function mockSuccess(type) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var response = options.response,
      delay = options.delay,
      actionCustomData = options.actionCustomData,
      debounce = options.debounce,
      abort = options.abort;
  return mockRequest({
    type: type,
    success: true,
    response: response,
    delay: delay,
    actionCustomData: actionCustomData,
    debounce: debounce,
    abort: abort
  });
};
/**
 * Action creator for mocking an error api request
 * @param {string} type Redux action type
 * @param {Object} options Optional, additional options to pass to the request
 * @param {number} options.code Numeric code which correlates to the type of error returned
 * @param {string} options.message Error message to return
 * @param {string|Array} options.data Additional data which correlates to the error
 * @param {number} options.delay Optional, time delay (ms) for the mock request to take, default 100ms
 * @param {boolean} options.preventNotification Optional, prevents error notifications being sent by the API middleware, default false
 * @param {number|boolean} options.debounce Optional, debounces multiple API requests with the same type in milliseconds. A positive value uses trailing-edge debounce, a negative value uses leading-edge debounce, false or zero indicates no debounce
 * @param {ApiProvider} options.abort Optional, instance of ApiProvider class which can be used to abort the API request before it completes
 * @returns {Object} Redux action object
 */


var mockError = function mockError(type) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var code = options.code,
      message = options.message,
      data = options.data,
      delay = options.delay,
      preventNotification = options.preventNotification,
      debounce = options.debounce,
      abort = options.abort;
  var error = code || message || data ? [{
    code: code,
    message: message,
    data: data
  }] : "";
  return mockRequest({
    type: type,
    success: false,
    error: error,
    delay: delay,
    preventErrorNotification: preventNotification,
    debounce: debounce,
    abort: abort
  });
}; // helper for mocking array data


var MockArrayData = /*#__PURE__*/_createClass(function MockArrayData() {
  var _this = this;

  var initialData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var sortFunc = arguments.length > 1 ? arguments[1] : undefined;

  _classCallCheck(this, MockArrayData);

  _defineProperty(this, "_data", void 0);

  _defineProperty(this, "_sort", void 0);

  _defineProperty(this, "get", function () {
    var data = _toConsumableArray(_this._data);

    if (_this._sort) data.sort(_this._sort);
    return data;
  });

  _defineProperty(this, "getPaged", function (pageSize, pageNumber) {
    var data = _this.get();

    return {
      results: _this._paginate(data, pageSize, pageNumber),
      totalResults: data.length
    };
  });

  _defineProperty(this, "filter", function (func) {
    var data = _this._data.filter(func);

    if (_this._sort) data.sort(_this._sort);
    return data;
  });

  _defineProperty(this, "filterPaged", function (func, pageSize, pageNumber) {
    var data = _this.filter(func);

    return {
      results: _this._paginate(data, pageSize, pageNumber),
      totalResults: data.length
    };
  });

  _defineProperty(this, "_paginate", function (array, pageSize, pageNumber) {
    return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  });

  _defineProperty(this, "add", function (item) {
    _this._data = [].concat(_toConsumableArray(_this._data), [item]);
  });

  _defineProperty(this, "addMany", function (items) {
    _this._data = [].concat(_toConsumableArray(_this._data), _toConsumableArray(items));
  });

  _defineProperty(this, "remove", function (item) {
    var i = _this._data.indexOf(item);

    if (i >= 0) {
      _this._data.splice(i, 1);
    }
  });

  _defineProperty(this, "removeWhere", function (func) {
    var i = _this._data.length;

    while (i--) {
      if (func(_this._data[i], i, _this._data)) {
        _this._data.splice(i, 1);
      }
    }
  });

  _defineProperty(this, "replaceFirst", function (func, item) {
    var i = 0;

    while (i < _this._data.length) {
      if (func(_this._data[i], i, _this._data)) {
        _this._data.splice(i, 1, item);

        return true;
      }

      i++;
    }

    return false;
  });

  _defineProperty(this, "update", function (searchFunc, updateFunc) {
    var i = _this._data.length;
    var updated = false;

    while (i--) {
      if (searchFunc(_this._data[i], i, _this._data)) {
        var updatedItem = updateFunc(_this._data[i], i);

        _this._data.splice(i, 1, updatedItem);

        updated = true;
      }
    }

    return updated;
  });

  _defineProperty(this, "find", function (func) {
    return _this._data.find(func);
  });

  this._data = initialData;
  this._sort = sortFunc;
});

export { MOCK_REQUEST, MockArrayData, mockError, mockRequest, mockSuccess };
